import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { BookOpen, Users, ClipboardCheck, TrendingUp, Plus, ArrowRight } from "lucide-react";

function StatCard({ label, value, tone = "indigo", testid, icon: Icon }) {
  const tones = {
    indigo: "bg-indigo-50 text-indigo-700",
    amber: "bg-amber-50 text-amber-700",
    emerald: "bg-emerald-50 text-emerald-700",
    rose: "bg-rose-50 text-rose-700",
  };
  return (
    <div className="qs-card p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">{label}</p>
          <p className="font-display text-3xl font-black text-slate-900 mt-2">{value}</p>
        </div>
        <div className={`w-10 h-10 rounded-xl grid place-items-center ${tones[tone]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export default function TeacherDashboard() {
  const [stats, setStats] = useState(null);
  const [exams, setExams] = useState([]);
  useEffect(() => {
    api.get("/teacher/stats").then((r) => setStats(r.data));
    api.get("/exams").then((r) => setExams(r.data));
  }, []);

  return (
    <AppShell title="Teacher hub" subtitle="Manage your exams, classes and student progress.">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="teacher-stats">
        <StatCard label="Exams" value={stats?.exams ?? 0} icon={BookOpen} tone="indigo" testid="teacher-stat-exams" />
        <StatCard label="Classes" value={stats?.classes ?? 0} icon={Users} tone="amber" testid="teacher-stat-classes" />
        <StatCard label="Attempts" value={stats?.attempts ?? 0} icon={ClipboardCheck} tone="emerald" testid="teacher-stat-attempts" />
        <StatCard label="Avg Score" value={`${stats?.average_score ?? 0}%`} icon={TrendingUp} tone="rose" testid="teacher-stat-avg" />
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 qs-card p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-display text-lg font-bold text-slate-900">Your exams</h3>
              <p className="text-sm text-slate-500">Everything you&rsquo;ve authored.</p>
            </div>
            <Link to="/teacher/exams/new" className="btn-primary text-sm" data-testid="quick-new-exam">
              <Plus className="w-4 h-4" /> New exam
            </Link>
          </div>
          <div className="mt-4 divide-y divide-slate-100">
            {exams.slice(0, 6).map((e) => (
              <div key={e.id} className="py-3 flex items-center gap-4" data-testid={`teacher-exam-row-${e.id}`}>
                <div className="w-11 h-11 rounded-xl bg-indigo-50 text-indigo-600 grid place-items-center">
                  <BookOpen className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800 truncate">{e.title}</p>
                  <p className="text-xs text-slate-500">
                    {e.subject} · {e.duration_min}m · {e.questions?.length || 0} Q · {e.is_published ? "Published" : "Draft"}
                  </p>
                </div>
                <Link to={`/teacher/exams/${e.id}`} className="text-sm font-semibold text-indigo-600" data-testid={`teacher-view-exam-${e.id}`}>
                  Manage <ArrowRight className="w-3.5 h-3.5 inline" />
                </Link>
              </div>
            ))}
            {exams.length === 0 && (
              <p className="text-sm text-slate-500 py-6 text-center">You haven&rsquo;t created any exams yet.</p>
            )}
          </div>
        </div>

        <div className="qs-card p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg font-bold text-slate-900">Recent attempts</h3>
            <Link to="/teacher/attempts" className="text-xs font-semibold text-indigo-600 hover:underline" data-testid="link-all-attempts">
              Grade all
            </Link>
          </div>
          <div className="mt-4 space-y-3">
            {(stats?.recent_attempts || []).map((a) => (
              <Link
                key={a.id}
                to={`/teacher/attempts/${a.id}`}
                className="block p-3 rounded-xl border border-slate-100 bg-slate-50/50 hover:bg-slate-100 transition"
                data-testid={`teacher-recent-attempt-${a.id}`}
              >
                <p className="font-semibold text-sm text-slate-800 truncate">{a.student_name}</p>
                <p className="text-xs text-slate-500 truncate">{a.exam_title}</p>
                <p className="text-sm font-bold text-indigo-600 mt-1">{a.score_percent}%</p>
              </Link>
            ))}
            {(!stats?.recent_attempts || stats.recent_attempts.length === 0) && (
              <p className="text-sm text-slate-500 text-center py-6">No student attempts yet.</p>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
